import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { utils, writeFile } from 'xlsx';
import { ReactFlow, Background, Controls, useNodesState, useEdgesState, ConnectionMode, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import RoleNode from './RoleNode';
import { SmartBezierEdge } from './organization-editor/SmartBezierEdge';
import { defaultEdgeOptions } from './organization-editor/EdgeOptions';
import type { Candidate } from "@/types/candidate";
import { Card } from "@/components/ui/card";
import { ExternalLink, Percent, FileText, ChevronDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const nodeTypes = {
  roleNode: RoleNode
};

const edgeTypes = {
  smartBezier: SmartBezierEdge
};

// Standardized sorting function used throughout the component
const sortCandidatesByMatchAndId = (candidates: Candidate[]) => {
  return candidates
    .slice() // Create a copy to avoid mutating original array
    .sort((a, b) => {
      if (a.matchPercentage !== b.matchPercentage) {
        return b.matchPercentage - a.matchPercentage; // Higher percentage first
      }
      
      // If match percentages are equal, sort by candidate_id ascending
      return (a.candidate_id || 0) - (b.candidate_id || 0);
    });
};

export const Summary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [projectName, setProjectName] = useState("");
  const [selectedNodeRole, setSelectedNodeRole] = useState<string | null>(null);
  const [selectedRoleCandidates, setSelectedRoleCandidates] = useState<Candidate[]>([]);
  const [roleDescriptions, setRoleDescriptions] = useState<Record<string, any>>({});

  const fetchProjectDetails = async () => {
    if (!projectId) return;
    try {
      const {
        data,
        error
      } = await supabase.from('project_requirements').select('name').eq('project_id', projectId).maybeSingle();
      if (error) {
        console.error('Error fetching project details:', error);
        return;
      }
      if (data) {
        setProjectName(data.name);
      }
    } catch (error) {
      console.error('Error in fetchProjectDetails:', error);
    }
  };

  const fetchRoleDescriptions = async () => {
    if (!projectId) return;
    try {
      const {
        data,
        error
      } = await supabase.from('role_jd').select('role_title, role_description').eq('project_id', projectId);
      if (error) {
        console.error('Error fetching role descriptions:', error);
        return;
      }
      if (data) {
        const descriptions = data.reduce((acc, curr) => ({
          ...acc,
          [curr.role_title]: curr.role_description
        }), {});
        setRoleDescriptions(descriptions);
      }
    } catch (error) {
      console.error('Error in fetchRoleDescriptions:', error);
    }
  };

  useEffect(() => {
    const fetchCandidates = async () => {
      if (!projectId) return;

      try {
        // Fetch candidates with consistent sorting
        const { data, error } = await supabase
          .from('candidates')
          .select('*')
          .eq('project_id', projectId)
          .order('role_title')
          .order('match_percentage', { ascending: false }) // Higher match percentage first
          .order('candidate_id', { ascending: true }); // Then by candidate_id ascending

        if (error) {
          console.error('Error fetching candidates:', error);
          return;
        }

        if (data) {
          const transformedCandidates: Candidate[] = data.map(c => ({
            id: c.id,
            project_name: c.project_name,
            roleTitle: c.role_title || '',
            pdfUrl: '',
            matchPercentage: parseInt(c.match_percentage || '0'),
            summary: c.summary || '',
            differentiators: Array.isArray(c.differentiators) ? c.differentiators.map(d => String(d)).filter(Boolean) : [],
            linkedinUrl: c.linkedin_url || '',
            openToWork: c.open_to_work || false,
            profilePictureUrl: c.profile_picture_url,
            candidate_name: c.candidate_name || '',
            role_title: c.role_title,
            match_percentage: c.match_percentage,
            linkedin_url: c.linkedin_url,
            candidate_id: c.candidate_id,
            open_to_work: c.open_to_work,
            profile_picture_url: c.profile_picture_url,
            project_id: c.project_id
          }));

          console.log(`[Summary] Fetched ${transformedCandidates.length} candidates:`, transformedCandidates.map(c => ({
            name: c.candidate_name,
            role: c.role_title,
            matchPercentage: c.matchPercentage,
            candidate_id: c.candidate_id
          })));

          setCandidates(transformedCandidates);
        }
      } catch (error) {
        console.error('Error in fetchCandidates:', error);
      }
    };

    const fetchOrganizationStructure = async () => {
      if (!projectId) return;
      try {
        const {
          data: structureData,
          error
        } = await supabase.from('organization_structures').select('nodes, edges').eq('project_id', projectId).maybeSingle();
        if (error) {
          console.error('Error fetching organization structure:', error);
          return;
        }
        if (structureData && Array.isArray(structureData.nodes)) {
          const typedNodes = structureData.nodes.map((node: any) => ({
            ...node,
            type: 'roleNode',
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top
          }));
          setNodes(typedNodes);
        }
        if (structureData && Array.isArray(structureData.edges)) {
          const typedEdges = structureData.edges.map((edge: any) => ({
            ...edge,
            type: 'smartBezier',
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top
          }));
          setEdges(typedEdges);
        }
      } catch (error) {
        console.error('Error in fetchOrganizationStructure:', error);
      }
    };

    fetchProjectDetails();
    fetchRoleDescriptions();
    fetchCandidates();
    fetchOrganizationStructure();
  }, [projectId, setNodes, setEdges]);

  const handleNodeClick = useCallback(async (event: any, node: any) => {
    if (!projectId || !node.data?.label) return;

    setSelectedNodeRole(node.data.label);
    
    // Filter candidates by role and apply consistent sorting
    const roleCandidates = candidates
      .filter(c => c.role_title === node.data.label);
    
    const sortedRoleCandidates = sortCandidatesByMatchAndId(roleCandidates).slice(0, 3);

    console.log(`[Summary] Selected role: ${node.data.label}, showing candidates:`, sortedRoleCandidates.map(c => ({
      name: c.candidate_name,
      matchPercentage: c.matchPercentage,
      candidate_id: c.candidate_id
    })));
    
    setSelectedRoleCandidates(sortedRoleCandidates);
  }, [projectId, candidates]);

  const handleBack = () => {
    if (!projectId) return;
    navigate(`/dashboard?step=4&projectId=${projectId}`);
  };

  const handleFinish = () => {
    toast.success("Project completed successfully!");
    navigate("/dashboard?step=1");
  };

  const handleDownload = () => {
    if (!nodes.length) {
      toast.error("No data available to download");
      return;
    }

    try {
      const uniqueNodes = nodes.reduce((acc: any[], node: any) => {
        const existingNode = acc.find(n => n.data.label === node.data.label);
        if (!existingNode) {
          acc.push(node);
        }
        return acc;
      }, []);

      const excelData = uniqueNodes.map((node: any, index) => {
        // Get top 3 candidates for this role with consistent sorting
        const roleCandidates = candidates
          .filter(c => c.role_title === node.data.label);
        
        const topCandidates = sortCandidatesByMatchAndId(roleCandidates).slice(0, 3);

        return {
          'SN': index + 1,
          'Role Title': node.data.label,
          'Job Description': roleDescriptions[node.data.label]?.pdf_url ? 'Available' : 'Not Available',
          'Candidate 1': topCandidates[0] ? {
            text: topCandidates[0].candidate_name,
            hyperlink: topCandidates[0].linkedin_url || ''
          } : '-',
          'Candidate 2': topCandidates[1] ? {
            text: topCandidates[1].candidate_name,
            hyperlink: topCandidates[1].linkedin_url || ''
          } : '-',
          'Candidate 3': topCandidates[2] ? {
            text: topCandidates[2].candidate_name,
            hyperlink: topCandidates[2].linkedin_url || ''
          } : '-'
        };
      });

      const ws = utils.json_to_sheet(excelData);
      excelData.forEach((row, rowIndex) => {
        const realRowIndex = rowIndex + 1;
        ['Candidate 1', 'Candidate 2', 'Candidate 3'].forEach((colName, colIndex) => {
          const cellRef = utils.encode_cell({
            r: realRowIndex,
            c: colIndex + 3
          });
          const cellValue = row[colName];
          if (typeof cellValue === 'object' && cellValue.text && cellValue.hyperlink) {
            ws[cellRef] = {
              f: `=HYPERLINK("${cellValue.hyperlink}","${cellValue.text}")`,
              t: 's'
            };
          }
        });
      });

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Roles and Candidates");

      const colWidths = [
        { wch: 5 },
        { wch: 30 },
        { wch: 15 },
        { wch: 50 },
        { wch: 50 },
        { wch: 50 }
      ];
      ws['!cols'] = colWidths;

      writeFile(wb, `${projectName || 'project'}_roles_and_candidates.xlsx`);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error('Error generating Excel file:', error);
      toast.error("Failed to generate Excel file");
    }
  };

  const formatCandidateCell = (candidate: Candidate | null) => {
    if (!candidate) return null;

    return (
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-primary-light">
          {candidate.candidate_name}
        </span>
      </div>
    );
  };

  const renderTableContent = () => {
    if (!nodes.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-[10px] text-left text-primary-light p-1">
            No roles found in the organization structure.
          </TableCell>
        </TableRow>
      );
    }

    return nodes.map((node: any, index) => {
      // Get top 3 candidates for this role with consistent sorting
      const roleCandidates = candidates
        .filter(c => c.role_title === node.data.label);
      
      const topCandidates = sortCandidatesByMatchAndId(roleCandidates).slice(0, 3);

      const roleDescription = roleDescriptions[node.data.label];

      console.log(`[Summary] Table row for role ${node.data.label}:`, topCandidates.map(c => ({
        name: c.candidate_name,
        matchPercentage: c.matchPercentage,
        candidate_id: c.candidate_id
      })));

      return (
        <TableRow 
          key={node.id} 
          className="transition-colors bg-secondary-light/5 hover:bg-primary/20"
        >
          <TableCell className="text-[10px] text-left p-1 text-primary-light">
            {index + 1}
          </TableCell>
          <TableCell className="text-[10px] text-left p-1 text-primary-light">
            {node.data.label}
          </TableCell>
          <TableCell className="text-left p-1">
            {roleDescription && typeof roleDescription === 'object' && roleDescription.pdf_url ? (
              <a 
                href={roleDescription.pdf_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-1 text-[10px] text-primary-light hover:text-primary"
                onClick={e => e.stopPropagation()}
              >
                <FileText className="w-3 h-3" />
                View JD
              </a>
            ) : (
              <span className="text-[10px] text-primary-light/50">-</span>
            )}
          </TableCell>
          <TableCell className="text-[10px] text-left p-1 text-primary-light">
            {formatCandidateCell(topCandidates[0])}
          </TableCell>
          <TableCell className="text-[10px] text-left p-1 text-primary-light">
            {formatCandidateCell(topCandidates[1])}
          </TableCell>
          <TableCell className="text-[10px] text-left p-1 text-primary-light">
            {formatCandidateCell(topCandidates[2])}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className="my-[2px] py-[9px] h-[510px] border-1 border-primary">
      <div className="space-y-2 bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-4 mt-2 py-[3px] my-[17px]">
        <div className="flex justify-center items-center gap-2 py-[4px]">
          <h3 className="text-white text-[11px]">{projectName}</h3>
          <span className="text-primary/80 text-[11px]">•</span>
          <p className="text-primary/80 text-[11px]">Project ID: {projectId}</p>
        </div>

        <div className="flex gap-4">
          <div className="w-1/2 h-[400px] border-2 border-dashed border-primary/20 rounded-lg overflow-hidden bg-primary/5">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1.5 }}
              minZoom={0.1}
              maxZoom={1.5}
              style={{ background: 'transparent' }}
              connectionMode={ConnectionMode.Loose}
              elevateNodesOnSelect={true}
              proOptions={{ hideAttribution: true }}
              onNodeClick={handleNodeClick}
            >
              <Background />
              <Controls />
            </ReactFlow>
          </div>

          <div className="w-1/2 h-[400px]">
            <div className="text-[11px] font-medium text-white mb-2 sticky top-0 bg-secondary/50 backdrop-blur-lg py-2">
              {selectedNodeRole ? `Top Candidates for ${selectedNodeRole}` : ''}
            </div>
            <div className="h-[340px] overflow-y-auto space-y-2">
              {!selectedNodeRole && (
                <div className="h-full flex items-center justify-center text-[11px] text-primary-light">
                  Select a node to display the top 3 candidates for each role
                </div>
              )}
              {selectedRoleCandidates.map((candidate, index) => (
                <Card key={candidate.id} className="p-3 bg-secondary-light/5 border-primary">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-[11px] text-white">
                        {candidate.candidate_name}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] ${candidate.openToWork ? 'bg-green-500/20 text-white' : 'bg-red-500/20 text-white'}`}>
                          {candidate.openToWork ? 'Open to work' : 'Not open'}
                        </span>
                        <span className="flex items-center gap-1 text-white text-[11px]">
                          <Percent className="w-3 h-3" />
                          {candidate.matchPercentage}
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-primary-light text-left">{candidate.summary}</p>
                    {candidate.differentiators && candidate.differentiators.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[11px] font-medium text-primary-light text-left">Key Differentiators</div>
                        <ul className="space-y-0.5">
                          {candidate.differentiators
                            .filter(diff => !diff.startsWith(":"))
                            .map((diff, idx) => (
                              <li key={idx} className="text-[11px] text-white flex items-start gap-1">
                                <span className="text-primary min-w-[8px]">•</span>
                                <span className="text-left">{diff}</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {candidate.linkedinUrl && (
                      <a 
                        href={candidate.linkedinUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-[11px] text-purple-500 hover:underline inline-flex items-center gap-1"
                      >
                        LinkedIn Profile <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </Card>
              ))}
              {selectedNodeRole && selectedRoleCandidates.length === 0 && (
                <div className="text-[11px] text-primary-light text-center">
                  No candidates found for this role
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="rounded-md border border-primary/20">
            <Collapsible defaultOpen={false}>
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-center gap-2 p-2 hover:bg-primary/10">
                  <h3 className="text-[12px] font-medium text-primary-light">Roles and Candidates Overview</h3>
                  <ChevronDown className="h-4 w-4 text-primary-light" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary/30 hover:bg-primary/30">
                      <TableHead className="text-[10px] text-white font-semibold text-center p-1">SN</TableHead>
                      <TableHead className="text-[10px] text-white font-semibold text-left p-1">Role Title</TableHead>
                      <TableHead className="text-[10px] text-white font-semibold text-left p-1">Job Description</TableHead>
                      <TableHead className="text-[10px] text-white font-semibold text-left p-1">Candidate 1</TableHead>
                      <TableHead className="text-[10px] text-white font-semibold text-left p-1">Candidate 2</TableHead>
                      <TableHead className="text-[10px] text-white font-semibold text-left p-1">Candidate 3</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renderTableContent()}
                  </TableBody>
                </Table>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-4 pb-0 my-0 py-0">
        <Button onClick={handleBack} className="w-32 bg-white text-secondary hover:bg-white/90">
          Back
        </Button>
        <Button onClick={handleDownload} className="w-32 bg-indigo-300 hover:bg-indigo-200">
          Download
        </Button>
        <Button onClick={handleFinish} className="w-32">
          Finish
        </Button>
      </div>
    </div>
  );
};
